import { Auth } from 'aws-amplify';
import React, { FC, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../index.module.scss';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'src/pages/paths';
import { Button, notification, Form, Input } from 'src/common';
import { ONLY_DIGITS_REGEX } from 'src/helpers/constants';
import AuthBack from '../AuthBack';
import { StorageKey } from 'src/types/enums';
import { useApp } from 'src/models/auth';
import { ownership } from 'src/models/ownership';

interface FormState {
  email: string;
  code: string;
}

export const ConfirmSignUpPage: FC = () => {
  const { t } = useTranslation();
  const { email, password, handleFreeUser } = useApp();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const from = sessionStorage.getItem(StorageKey.FromLogin);
  const redirectTo = useMemo(() => (!!from ? from : Paths.FREE_HOME_PATH), [from]);
  const resendCode = useCallback(() => {
    email &&
      Auth.resendSignUp(email)
        .then(() => {
          notification.success({
            message: t('Auth.checkEmailForCode'),
            description: t('Auth.checkEmailForCodeDescription')
          });
        })
        .catch(() => {
          notification.error({
            message: t('Auth.codeNotSent'),
            description: t('Common.unknownErrorDesc')
          });
        });
  }, [email, t]);

  const initialValues = useMemo((): FormState => {
    return {
      email: email ?? '',
      code: ''
    };
  }, [email]);

  const onFinish = useCallback(
    (values: FormState) => {
      const { code, email } = values;
      setIsLoading(true);
      Auth.confirmSignUp(email, code, {
        clientMetadata: { [`custom:domain`]: ownership.name }
      })
        .then(async () => {
          if (password) {
            const user = await Auth.signIn(email, password);
            try {
              handleFreeUser(user);
              sessionStorage.removeItem(StorageKey.FromLogin);
              navigate(redirectTo, { replace: true });
            } catch (error) {
              navigate(Paths.SIGN_IN, { replace: true });
            }
          } else {
            navigate(Paths.SIGN_IN, { replace: true });
          }
        })
        .catch(() => {
          notification.error({
            message: t('Auth.notSignedUp'),
            description: t('Common.unknownErrorDesc')
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [handleFreeUser, navigate, password, redirectTo, t]
  );

  return (
    <Form form={form} initialValues={initialValues} onFinish={onFinish} layout={'vertical'} className={styles.form}>
      <div className={styles.heading}>
        <h3 className={styles.title}>
          <span>{t('Auth.confirmSignUp.title')}</span>
        </h3>
        <span className={styles.desc}>{t('Auth.confirmSignUp.desc')}</span>
      </div>

      <Form.Item name="email" label={t('Common.email')}>
        <Input name="email" placeholder={t('Common.email')} disabled={!!email} type={'email'} autoComplete={'email'} />
      </Form.Item>
      <div className={styles.codeControl}>
        <Form.Item
          name="code"
          label={t('Auth.common.labels.code')}
          rules={[
            { required: true, message: t('Common.inputRequired', { inputName: t('Auth.code') }) },
            { pattern: new RegExp(ONLY_DIGITS_REGEX), message: t('Auth.common.errors.codePolicy') }
          ]}
        >
          <Input
            name="code"
            placeholder={t('Auth.common.placeholders.code')}
            autoComplete={'one-time-code'}
            type={'number'}
          />
        </Form.Item>
        <Button type={'default'} onClick={resendCode}>
          {t('Auth.common.buttons.resend')}
        </Button>
      </div>
      <div className={styles.actions}>
        <Button type="primary" htmlType="submit" loading={isLoading}>
          {t('Auth.common.buttons.confirm')}
        </Button>
        <AuthBack />
      </div>
    </Form>
  );
};

export default ConfirmSignUpPage;
